// ═══════════════════════════════════════════════════════════════
// MÓDULO DE ISR - INTEGRADO CON MOTOR DE TSS
// Usa calculateSS del hook de Seguridad Social
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { Calculator, DollarSign, TrendingUp, Info, ChevronDown, ChevronUp, Calendar, RefreshCw, ChevronRight, Home, GraduationCap, Users, CreditCard, Gift } from 'lucide-react';
import { useSocialSecurityConfig } from '../../../hooks/useSocialSecurityConfig';
import { useVariableIncomes } from '../../../hooks/useVariableIncomes';
import useEmployees from '../../../hooks/useEmployees';
import { getTaxBenefits } from '../../../api/taxBenefits';
import { getHealthDependentsSummary } from '../../../api/relatedPersons';
import { payrollConfigAPI } from '../../../api/payrollConfig';
import { prestacionesAPI } from '../../../api/prestaciones';
import { useCompany } from '../../../contexts/CompanyContext';
import { bonificacionesAPI } from '../../../api/bonificaciones';
import { getCreditosISR } from '../../../api/creditosISR';
import { getIsrRetenido } from '../../../api/payroll';
import { salaryHistoryAPI } from '../../../api/salaryHistory';

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SCALE_BADGE = {
  'Exento': 'bg-green-100 text-green-700',
  '15%':    'bg-blue-100 text-blue-700',
  '20%':    'bg-orange-100 text-orange-700',
  '25%':    'bg-red-100 text-red-700',
};

const ISR_BRACKETS = [
  { label: 'Exento',   range: 'Hasta RD$ 416,220',   rate: '0%',  cls: 'text-green-700'  },
  { label: 'Escala 1', range: 'Hasta RD$ 624,329',   rate: '15%', cls: 'text-blue-700'   },
  { label: 'Escala 2', range: 'Hasta RD$ 867,123',   rate: '20%', cls: 'text-orange-700' },
  { label: 'Escala 3', range: 'Más de RD$ 867,123',  rate: '25%', cls: 'text-red-700'    },
];

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, valueClass = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-gray-400" />
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      <p className={`text-base font-bold ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ISRModule() {
  const { employees } = useEmployees();
  const { getVariableIncomes } = useVariableIncomes();
  const { calculateSS, getEffectiveConfig, globalConfig } = useSocialSecurityConfig();
  const { currentCompany } = useCompany();

  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [calculations, setCalculations]     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [showDetails, setShowDetails]       = useState({});
  const [showScales, setShowScales]         = useState(false);
  const [taxBenefitsData, setTaxBenefitsData]         = useState({});
  const [capitaData, setCapitaData]                   = useState({});
  const [historicalSalaries, setHistoricalSalaries]   = useState({});
  // salariosMensuales: Map employeeId → salario mensual vigente al fin del período (desde salary_history/contrato)
  const [salariosMensuales, setSalariosMensuales]     = useState({});
  // bonificacionesPorEmpleado: Map employeeId → monto_final bruto de bonif. del año
  const [bonificacionesPorEmpleado, setBonificacionesPorEmpleado] = useState({});
  // creditosPorEmpleado: Map employeeId → saldo disponible en creditos_isr activos
  const [creditosPorEmpleado, setCreditosPorEmpleado] = useState({});
  // isrRetenidoPorEmpleado: Map employeeId → { isr_retenido_total, isr_q1, isr_q2 } de nóminas cerradas
  const [isrRetenidoPorEmpleado, setIsrRetenidoPorEmpleado] = useState({});
  const calcVersion = useRef(0);

  // Escalas de ISR 2024
  const ISR_SCALES = { exento: 416220.00, escala1: 624329.01, escala2: 867123.01 };

  // Cargar beneficios fiscales activos del año en curso
  useEffect(() => {
    const fiscalYear = parseInt((selectedPeriod || '').split('-')[0]) || new Date().getFullYear();
    getTaxBenefits({ fiscalYear, status: 'active' })
      .then(data => {
        // Guardar lista completa por empleado para aplicar periodicidad correctamente
        const map = {};
        (data?.benefits || []).forEach(r => {
          if (!map[r.employee_id]) map[r.employee_id] = [];
          map[r.employee_id].push({
            type:          r.benefit_type,
            annual:        parseFloat(r.annual_amount || 0),
            periodicidad:  r.periodicidad  || 'mensual',
            mesAplicacion: parseInt(r.mes_aplicacion || 12, 10),
          });
        });
        setTaxBenefitsData(map);
      })
      .catch(() => setTaxBenefitsData({}));
  }, [selectedPeriod]);

  // Cargar cápita: dependent_count × capita_monto (misma lógica que SS module y procesador de nómina)
  useEffect(() => {
    Promise.all([payrollConfigAPI.getParametros(), getHealthDependentsSummary()])
      .then(([params, rows]) => {
        const capParam  = Array.isArray(params) ? params.find(p => p.parametro === 'capita_monto') : null;
        const montoUnit = capParam ? parseFloat(capParam.valor) || 0 : 0;
        const map = {};
        rows.forEach(r => { map[r.employee_id] = parseInt(r.dependent_count || 0) * montoUnit; });
        setCapitaData(map);
      })
      .catch(() => setCapitaData({}));
  }, []);

  // Cargar ingresos variables de nóminas cerradas del período (comisiones, HE, etc.)
  useEffect(() => {
    if (!currentCompany?.id) { setHistoricalSalaries({}); return; }
    prestacionesAPI.getSalariosPeriodo(currentCompany.id, selectedPeriod)
      .then(map => setHistoricalSalaries(map || {}))
      .catch(() => setHistoricalSalaries({}));
  }, [selectedPeriod, currentCompany?.id]);

  // Cargar salario mensual vigente al último día del período desde salary_history/contrato.
  // Esta es la fuente correcta del salario base para ISR, independiente de si la nómina
  // es quincenal (que almacena salarioMensual/2 por período) o mensual.
  useEffect(() => {
    if (!currentCompany?.id || !selectedPeriod) { setSalariosMensuales({}); return; }
    // Último día del mes seleccionado
    const [yyyy, mm] = selectedPeriod.split('-');
    const lastDay = new Date(parseInt(yyyy), parseInt(mm), 0).getDate();
    const fechaFin = `${selectedPeriod}-${String(lastDay).padStart(2, '0')}`;
    salaryHistoryAPI.getBulkAtDate(currentCompany.id, fechaFin)
      .then(map => setSalariosMensuales(map || {}))
      .catch(() => setSalariosMensuales({}));
  }, [selectedPeriod, currentCompany?.id]);

  // Cargar bonificaciones anuales aprobadas/pagadas para el año del período seleccionado
  useEffect(() => {
    if (!currentCompany?.id) return;
    const anio = parseInt((selectedPeriod || '').split('-')[0], 10);
    if (!anio) return;

    bonificacionesAPI.getAll(currentCompany.id)
      .then(async ({ bonificaciones }) => {
        // Solo las que están aprobadas o pagadas relevantes al año en cuestión.
        // - Pagadas: usar el año de pago real (fecha_pago), NO el año fiscal de la bonificación.
        //   Una bonificación de 2024 pagada en marzo 2025 debe aparecer en ISR 2025.
        // - Aprobadas (sin pagar): usar fecha_pago_prevista si existe, si no b.anio.
        //   Una bonificación fiscal 2026 con pago previsto en feb 2027 debe aparecer en ISR 2027.
        const delAnio = (bonificaciones || []).filter(b => {
          if (!['pagado', 'aprobado', 'borrador'].includes(b.estado)) return false;
          if (b.estado === 'borrador' && !b.isr_calculado) return false;
          // Siempre usar fecha_pago_prevista para el período ISR —
          // es la fecha fiscal autorizada independientemente de cuándo se procesó.
          const year = b.fecha_pago_prevista
            ? parseInt(String(b.fecha_pago_prevista).slice(0, 4), 10)
            : b.anio;
          return year === anio;
        });
        if (!delAnio.length) { setBonificacionesPorEmpleado({}); return; }

        // Acumular montos de todas las bonificaciones del año (puede haber más de una)
        const map = {};
        for (const bon of delAnio) {
          const detalle = await bonificacionesAPI.getDetalle(bon.id).catch(() => []);
          const rows = Array.isArray(detalle) ? detalle : detalle?.detalle || [];
          rows.forEach(d => {
            if (d.estado === 'excluido') return;
            const monto = parseFloat(d.monto_final || 0);
            map[d.employee_id] = (map[d.employee_id] || 0) + monto;
          });
        }
        setBonificacionesPorEmpleado(map);
      })
      .catch(() => setBonificacionesPorEmpleado({}));
  }, [selectedPeriod, currentCompany?.id]); // eslint-disable-line

  // Cargar créditos ISR activos (saldo a favor) por empleado
  useEffect(() => {
    if (!currentCompany?.id) return;
    getCreditosISR(currentCompany.id, { estado: 'activo' })
      .then(list => {
        const map = {};
        (list || []).forEach(c => {
          const saldo = parseFloat(c.saldo || 0);
          if (saldo > 0) map[c.employee_id] = (map[c.employee_id] || 0) + saldo;
        });
        setCreditosPorEmpleado(map);
      })
      .catch(() => setCreditosPorEmpleado({}));
  }, [currentCompany?.id]); // eslint-disable-line

  // Cargar ISR ya retenido en nóminas cerradas del período seleccionado
  useEffect(() => {
    if (!currentCompany?.id || !selectedPeriod) return;
    const [anio, mes] = selectedPeriod.split('-');
    getIsrRetenido(currentCompany.id, parseInt(mes, 10), parseInt(anio, 10))
      .then(data => setIsrRetenidoPorEmpleado(data || {}))
      .catch(() => setIsrRetenidoPorEmpleado({}));
  }, [selectedPeriod, currentCompany?.id]); // eslint-disable-line

  useEffect(() => {
    if (employees.length > 0 && globalConfig) {
      calculateAllISR(selectedPeriod);
    }
  }, [employees, selectedPeriod, globalConfig, taxBenefitsData, capitaData, historicalSalaries, salariosMensuales, bonificacionesPorEmpleado, creditosPorEmpleado, isrRetenidoPorEmpleado]); // eslint-disable-line

  const calculateISR = (rentaImponibleMensual) => {
    const rentaAnual = rentaImponibleMensual * 12;
    if (rentaAnual < ISR_SCALES.exento)  return 0;
    if (rentaAnual < ISR_SCALES.escala1) return ((rentaAnual - 416220.01) * 0.15) / 12;
    if (rentaAnual < ISR_SCALES.escala2) return (((rentaAnual - 624329.01) * 0.20) + 31216) / 12;
    return (((rentaAnual - 867123.01) * 0.25) + 79776) / 12;
  };

  const getISRScale = (rentaAnual) => {
    if (rentaAnual < ISR_SCALES.exento)  return 'Exento';
    if (rentaAnual < ISR_SCALES.escala1) return '15%';
    if (rentaAnual < ISR_SCALES.escala2) return '20%';
    return '25%';
  };

  const calculateAllISR = async (periodo) => {
    const myVersion = ++calcVersion.current;
    setLoading(true);
    setCalculations([]);

    try {
      const results = [];

      for (const emp of employees.filter(e => e.estado === 'activo')) {
        const [dataQ1, dataQ2] = await Promise.all([
          getVariableIncomes(emp.id, periodo, 'Q1').catch(() => null),
          getVariableIncomes(emp.id, periodo, 'Q2').catch(() => null),
        ]);

        if (myVersion !== calcVersion.current) return;

        // ── Lógica Q1/Q2 (igual que el procesador de nómina) ──────────────────
        // Si solo existe Q1: base ISR = salario + vars Q1 → ISR mensual / 2
        // Si existe Q2:      base ISR = salario + vars Q1 + vars Q2 → ISR mensual completo
        const hasQ2 = dataQ2 !== null;

        // Ingresos variables: desde nóminas cerradas del período si existen,
        // si no, desde payroll_incomes (datos ingresados pero no procesados).
        const historicalData = historicalSalaries[emp.id];
        let horasExtras, comisiones, incentivos, vacaciones, bonificaciones, otrosIngresos;

        // Salario base mensual:
        // - Si hay nóminas cerradas: usar salario_base de payroll_records (MAX(salario_base)*2).
        //   Esta es la fuente de verdad auditada — es exactamente lo que procesó payrollCalculation.js
        //   y lo que usa calcular-isr en el backend. Garantiza que ISR Total = ISR Retenido.
        // - Si no hay nóminas cerradas: usar salary_history → fallback a emp.salario.
        const salarioBase = (historicalData && historicalData.salario_base > 0)
          ? parseFloat(historicalData.salario_base)
          : (salariosMensuales[emp.id] ?? parseFloat(emp.salario) ?? 0);

        if (historicalData && typeof historicalData === 'object') {
          // Nóminas cerradas: usar variables reales (SUM Q1+Q2)
          horasExtras   = historicalData.horas_extras   || 0;
          comisiones    = historicalData.comisiones      || 0;
          incentivos    = historicalData.incentivos      || 0;
          vacaciones    = historicalData.vacaciones      || 0;
          bonificaciones= historicalData.bonificaciones  || 0;
          otrosIngresos = historicalData.otros_ingresos  || 0;
        } else {
          // Sin nóminas cerradas: sumar lo ingresado en payroll_incomes (Q1 + Q2 disponibles)
          horasExtras   = parseFloat(dataQ1?.horas_extras   || 0) + parseFloat(dataQ2?.horas_extras   || 0);
          comisiones    = parseFloat(dataQ1?.comisiones      || 0) + parseFloat(dataQ2?.comisiones      || 0);
          incentivos    = parseFloat(dataQ1?.incentivos      || 0) + parseFloat(dataQ2?.incentivos      || 0);
          vacaciones    = parseFloat(dataQ1?.vacaciones      || 0) + parseFloat(dataQ2?.vacaciones      || 0);
          bonificaciones= parseFloat(dataQ1?.bonificaciones  || 0) + parseFloat(dataQ2?.bonificaciones  || 0);
          otrosIngresos = parseFloat(dataQ1?.otros_ingresos  || 0) + parseFloat(dataQ2?.otros_ingresos  || 0);
        }

        // Bonificación anual bruta (sin INFOTEP) — se suma al base imponible ISR
        // solo si existe una bonificación aprobada/pagada para el año del período
        const bonificacionAnual = bonificacionesPorEmpleado[emp.id] || 0;

        const totalIngresos = salarioBase + horasExtras + comisiones + incentivos +
                              vacaciones + bonificaciones + otrosIngresos + bonificacionAnual;

        // Aplicar periodicidad: 'mensual' → annual/12, 'anual' → monto completo solo en mes configurado
        const mesDelPeriodo = parseInt((periodo || '').split('-')[1], 10) || 0;
        const benefitsList  = taxBenefitsData[emp.id] || [];
        let mortgageInterestMensual = 0, educationCreditMensual = 0, dependentCreditMensual = 0;
        let mortgageAnnual = 0, educationAnnual = 0, dependentAnnual = 0;
        for (const b of benefitsList) {
          const aplicado = b.periodicidad === 'mensual'
            ? b.annual / 12
            : (mesDelPeriodo === b.mesAplicacion ? b.annual : 0);
          if (b.type === 'mortgage_interest')   { mortgageInterestMensual += aplicado; mortgageAnnual += b.annual; }
          if (b.type === 'education_expense')   { educationCreditMensual  += aplicado; educationAnnual += b.annual; }
          if (b.type === 'dependent_allowance') { dependentCreditMensual  += aplicado; dependentAnnual += b.annual; }
        }
        const capitaMensual = capitaData[emp.id] || 0;

        let tssEmpleado = 0, tssNeto = 0, tssAsumido = 0, afpMensual = 0, sfsMensual = 0;

        try {
          const ssCalc = calculateSS({ salarioBase, comisiones, incentivos, employeeId: emp.id });
          if (ssCalc && typeof ssCalc.afpEmpleadoBruto === 'number') {
            afpMensual  = ssCalc.afpEmpleadoBruto;
            sfsMensual  = ssCalc.sfsEmpleadoBruto;
            tssEmpleado = afpMensual + sfsMensual;
            tssNeto     = ssCalc.totalEmpleado ?? 0;
            tssAsumido  = ssCalc.totalAbsorbido ?? 0;
          } else {
            tssEmpleado = salarioBase * 0.0591;
            tssNeto     = tssEmpleado;
          }
        } catch {
          tssEmpleado = salarioBase * 0.0591;
          tssNeto     = tssEmpleado;
        }

        // AFP + SFS + Cápita ARS + Interés hipotecario reducen la base imponible ISR
        const tssDeductibleISR  = tssEmpleado + capitaMensual + mortgageInterestMensual;
        const ingresosRegulares = totalIngresos - bonificacionAnual; // sin el bono anual

        // ── ISR REGULAR (base para comparar con Q1+Q2 de nómina) ──────────────
        // Los payrolls Q1/Q2 se procesan SIN conocer la bonificación anual.
        // → isrMensual se calcula sobre ingresos regulares solamente.
        // → Q1+Q2 debe igualar este valor (true-up del Q2 lo garantiza).
        const rentaImponibleCalculo = ingresosRegulares - tssDeductibleISR;
        const rentaAnual            = rentaImponibleCalculo * 12;
        const isrBruto              = Math.round(calculateISR(rentaImponibleCalculo));

        // ── ISR TOTAL DEL MES (ingresos reales incl. bonificación) ──────────────
        // La bonificación es ingreso real recibido ese mes (viene de nómina bonificación).
        // ISR Total = calculateISR(totalIngresos − TSS): lo que realmente corresponde retener.
        // La diferencia con isrMensual (regular) = porción ISR atribuible al bono.
        const rentaAnualConBonif = (totalIngresos - tssDeductibleISR) * 12;
        const isrConBonif        = bonificacionAnual > 0
          ? Math.max(0, Math.round(calculateISR(totalIngresos - tssDeductibleISR)) - Math.round(educationCreditMensual + dependentCreditMensual))
          : 0;

        // Renta imponible para DISPLAY = total ingresos (incl. bono) − TSS
        const rentaImponible = totalIngresos - tssDeductibleISR;

        // Educación y dependiente son créditos fiscales (se restan del ISR calculado)
        const isrCreditos = Math.round(educationCreditMensual + dependentCreditMensual);
        // isrMensual: ISR sobre ingresos regulares (sin bono) → para verificar Q1+Q2
        const isrMensual  = Math.max(0, isrBruto - isrCreditos);
        const escalaISR   = getISRScale(rentaAnual);

        // isrMensualTotal: ISR sobre TODOS los ingresos del período (incluyendo bono)
        // = lo que el usuario espera como "ISR del mes" completo
        const isrMensualTotal = bonificacionAnual > 0 ? isrConBonif : isrMensual;

        // ISR retenido en nóminas cerradas del período
        const retenidoData      = isrRetenidoPorEmpleado[emp.id] || {};
        const isrYaRetenido     = parseFloat(retenidoData.isr_retenido_total || 0);
        const isrRetenidoBonif  = parseFloat(retenidoData.isr_bonificacion   || 0);
        const isrRetenidoNomina = isrYaRetenido - isrRetenidoBonif; // solo Q1+Q2

        // ISR Bonif = ISR mensual total − ISR de nóminas (lo que corresponde al bono)
        const isrBonifProyectado = bonificacionAnual > 0 ? Math.max(0, isrMensualTotal - isrRetenidoNomina) : 0;
        const creditoFiscal     = creditosPorEmpleado[emp.id] || 0;

        // ISR pendiente = ISR total proyectado vs total retenido (nóminas + bonif)
        const isrARetener  = isrMensualTotal;
        const isrPendiente = Math.max(0, isrMensualTotal - isrYaRetenido);
        const isrFinal     = Math.max(0, isrMensualTotal - isrYaRetenido - creditoFiscal);
        const isrAcumulado = isrMensualTotal;

        results.push({
          empleadoId: emp.id,
          empleadoNombre: `${emp.nombre} ${emp.apellido}`,
          empleadoCodigo: emp.employee_id,
          departamento:   emp.departamento || 'N/A',
          salarioBase, horasExtras, comisiones, incentivos,
          vacaciones, bonificaciones, otrosIngresos, bonificacionAnual,
          totalIngresos,
          tssEmpleado, tssNeto, tssAsumido, afpMensual, sfsMensual, capitaMensual,
          mortgageInterestMensual, educationCreditMensual, dependentCreditMensual,
          mortgageAnnual, educationAnnual, dependentAnnual,
          tssDeductibleISR,
          isrBruto, isrCreditos,
          rentaImponible, rentaAnual, escalaISR,
          isrMensual,        // ISR regular (sin bono) — para check Q1+Q2
          isrMensualTotal,   // ISR total (con bono) — columna principal
          isrBonifProyectado,// ISR atribuible al bono = total − regular
          isrConBonif, rentaAnualConBonif,
          isrARetener, creditoFiscal, isrFinal, hasQ2,
          isrAcumulado, isrYaRetenido, isrRetenidoBonif, isrRetenidoNomina, isrPendiente,
          isrRetenidoQ1: parseFloat(retenidoData.isr_q1 || 0),
          isrRetenidoQ2: parseFloat(retenidoData.isr_q2 || 0),
          nominasProcesadas: parseInt(retenidoData.nominas_procesadas || 0),
        });
      }

      if (myVersion === calcVersion.current) {
        setCalculations(results);
        setLoading(false);
      }
    } catch (error) {
      if (myVersion === calcVersion.current) {
        console.error('Error ISR:', error);
        alert('Error al calcular ISR: ' + error.message);
        setLoading(false);
      }
    }
  };

  const toggleDetails = (id) =>
    setShowDetails(prev => ({ ...prev, [id]: !prev[id] }));

  const totals = calculations.reduce((acc, c) => ({
    totalIngresos:         acc.totalIngresos         + c.totalIngresos,
    tssTotal:              acc.tssTotal              + c.tssEmpleado + c.capitaMensual,
    rentaImponible:        acc.rentaImponible        + c.rentaImponible,
    isrTotal:              acc.isrTotal              + c.isrMensualTotal,   // total con bono
    isrARetenerTotal:      acc.isrARetenerTotal      + c.isrARetener,
    creditoFiscalTotal:    acc.creditoFiscalTotal    + c.creditoFiscal,
    isrFinalTotal:         acc.isrFinalTotal         + c.isrFinal,
    isrRetenidoNominaTotal:acc.isrRetenidoNominaTotal+ c.isrRetenidoNomina,
    isrRetenidoBonifTotal:   acc.isrRetenidoBonifTotal   + c.isrRetenidoBonif,
    isrBonifProyectadoTotal: acc.isrBonifProyectadoTotal + c.isrBonifProyectado,
    isrYaRetenidoTotal:      acc.isrYaRetenidoTotal      + c.isrYaRetenido,
    isrPendienteTotal:       acc.isrPendienteTotal       + c.isrPendiente,
  }), { totalIngresos: 0, tssTotal: 0, rentaImponible: 0, isrTotal: 0, isrARetenerTotal: 0, creditoFiscalTotal: 0, isrFinalTotal: 0, isrRetenidoNominaTotal: 0, isrRetenidoBonifTotal: 0, isrBonifProyectadoTotal: 0, isrYaRetenidoTotal: 0, isrPendienteTotal: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Calculando ISR...</p>
        </div>
      </div>
    );
  }

  const n = calculations.length;

  return (
    <div className="space-y-4">

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {n} empleado{n !== 1 ? 's' : ''}
        </span>

        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-gray-400" />
          <input
            type="month"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
        </div>

        <button
          onClick={() => setShowScales(!showScales)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
            showScales
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <Info size={13} />
          Escalas ISR
          {showScales ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>

        <button
          onClick={() => calculateAllISR(selectedPeriod)}
          className="ml-auto flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw size={13} />
          Recalcular
        </button>
      </div>

      {/* ── Scales panel (collapsible) ───────────────────────────── */}
      {showScales && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Escalas ISR 2024 · Ley 11-92 (base anual)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ISR_BRACKETS.map(({ label, range, rate, cls }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-sm font-bold ${cls}`}>{range}</p>
                <p className="text-xs text-gray-400 mt-0.5">{rate}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Stat cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={Calculator}
          label="ISR Mensual Total"
          value={`RD$ ${fmt(totals.isrTotal)}`}
          sub="Sal.+Vars+Bonif. → ISR proyectado"
          valueClass="text-red-600"
        />
        <StatCard
          icon={DollarSign}
          label="ISR Total Retenido"
          value={`RD$ ${fmt(totals.isrYaRetenidoTotal)}`}
          sub={totals.isrRetenidoBonifTotal > 0
            ? `Nóm: ${fmt(totals.isrRetenidoNominaTotal)} · Bonif: ${fmt(totals.isrRetenidoBonifTotal)}`
            : 'En nóminas Q1+Q2 del período'}
          valueClass="text-green-600"
        />
        <StatCard
          icon={TrendingUp}
          label="ISR Pendiente"
          value={`RD$ ${fmt(totals.isrPendienteTotal)}`}
          sub={totals.isrPendienteTotal === 0 ? 'Todo retenido ✓' : 'Por retener aún'}
          valueClass={totals.isrPendienteTotal === 0 ? 'text-green-600' : 'text-amber-600'}
        />
        <StatCard
          icon={CreditCard}
          label="ISR Final (con créditos)"
          value={`RD$ ${fmt(totals.isrFinalTotal)}`}
          sub={totals.creditoFiscalTotal > 0 ? `Créditos: − RD$ ${fmt(totals.creditoFiscalTotal)}` : 'Sin créditos activos'}
          valueClass={totals.creditoFiscalTotal > 0 ? 'text-blue-600' : 'text-red-600'}
        />
      </div>
      {/* ── Secondary stat cards ─────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard
          icon={TrendingUp}
          label="Total Ingresos"
          value={`RD$ ${fmt(totals.totalIngresos)}`}
          valueClass="text-gray-900"
        />
        <StatCard
          icon={DollarSign}
          label="TSS Deductible"
          value={`RD$ ${fmt(totals.tssTotal)}`}
          sub="AFP + SFS"
          valueClass="text-amber-600"
        />
        <StatCard
          icon={Calculator}
          label="Renta Imponible"
          value={`RD$ ${fmt(totals.rentaImponible)}`}
          sub="Ingresos − TSS"
          valueClass="text-blue-600"
        />
      </div>

      {/* ── Table ───────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="px-4 py-3 text-left   text-xs font-semibold uppercase tracking-wider">Empleado</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Salario Base</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Ing. Variables</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Total Ingresos</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">TSS</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Benef. Fiscales</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Renta Imponible</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Escala</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider">ISR Total</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider bg-green-700">ISR Retenido</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider bg-amber-600">ISR Pendiente</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider bg-blue-700">Crédito Fiscal</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold uppercase tracking-wider bg-blue-800">ISR Final</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {calculations.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12 text-center text-sm text-gray-400">
                    No hay empleados para calcular ISR
                  </td>
                </tr>
              ) : (
                calculations.map((calc) => {
                  const ingresosVariables = calc.horasExtras + calc.comisiones + calc.incentivos +
                                           calc.vacaciones  + calc.bonificaciones + calc.otrosIngresos +
                                           calc.bonificacionAnual;
                  const isExpanded = showDetails[calc.empleadoId];

                  return (
                    <React.Fragment key={calc.empleadoId}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">{calc.empleadoNombre}</p>
                          <p className="text-xs text-gray-400">{calc.empleadoCodigo}</p>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">
                          RD$ {fmt(calc.salarioBase)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700">
                          {ingresosVariables > 0
                            ? `RD$ ${fmt(ingresosVariables)}`
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                          RD$ {fmt(calc.totalIngresos)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className="text-amber-600 font-medium">
                            RD$ {fmt(calc.tssEmpleado + calc.capitaMensual)}
                          </span>
                          {calc.capitaMensual > 0 && (
                            <p className="text-xs text-amber-500">incl. cápita</p>
                          )}
                          {calc.tssAsumido > 0 && (
                            <p className="text-xs text-green-500">Asumido por empresa</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span
                              title={calc.mortgageInterestMensual > 0 ? `Interés hipotecario: RD$${fmt(calc.mortgageInterestMensual)}/mes` : 'Sin interés hipotecario'}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${calc.mortgageInterestMensual > 0 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-300'}`}
                            >
                              <Home size={11} />
                            </span>
                            <span
                              title={calc.educationCreditMensual > 0 ? `Gastos educativos: RD$${fmt(calc.educationCreditMensual)}/mes` : 'Sin gastos educativos'}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${calc.educationCreditMensual > 0 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-300'}`}
                            >
                              <GraduationCap size={11} />
                            </span>
                            <span
                              title={calc.dependentCreditMensual > 0 ? `Dependiente adicional: RD$${fmt(calc.dependentCreditMensual)}/mes` : 'Sin dependiente adicional'}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${calc.dependentCreditMensual > 0 ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-300'}`}
                            >
                              <Users size={11} />
                            </span>
                          </div>
                          {calc.isrCreditos > 0 && (
                            <p className="text-xs text-green-600 font-medium mt-0.5">-RD${fmt(calc.isrCreditos)}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700 font-medium">
                          RD$ {fmt(calc.rentaImponible)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${SCALE_BADGE[calc.escalaISR]}`}>
                            {calc.escalaISR}
                          </span>
                        </td>
                        {/* ISR Total = calculateISR(totalIngresos − TSS) */}
                        <td className="px-4 py-3 text-right text-sm font-bold text-red-600">
                          RD$ {fmt(calc.isrMensualTotal)}
                        </td>
                        {/* ISR Retenido = todo lo retenido en nóminas cerradas */}
                        <td className="px-4 py-3 text-right text-sm bg-green-50">
                          {calc.isrYaRetenido > 0 ? (
                            <span className="font-semibold text-green-700">RD$ {fmt(calc.isrYaRetenido)}</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        {/* ISR Pendiente = ISR Total − ISR Retenido */}
                        <td className="px-4 py-3 text-right text-sm bg-amber-50">
                          {calc.isrMensualTotal > 0 ? (
                            calc.isrPendiente === 0 ? (
                              <span className="text-green-600 font-semibold text-xs">Al día ✓</span>
                            ) : (
                              <span className="font-semibold text-amber-700">RD$ {fmt(calc.isrPendiente)}</span>
                            )
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm bg-blue-50">
                          {calc.creditoFiscal > 0 ? (
                            <>
                              <span className="font-semibold text-blue-700">− RD$ {fmt(calc.creditoFiscal)}</span>
                              <p className="text-xs text-blue-400 flex items-center justify-end gap-0.5 mt-0.5">
                                <CreditCard size={9} /> saldo a favor
                              </p>
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm bg-blue-50">
                          <span className={`font-bold ${calc.isrFinal === 0 && calc.isrARetener > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            RD$ {fmt(calc.isrFinal)}
                          </span>
                          {calc.isrFinal === 0 && calc.isrARetener > 0 && (
                            <p className="text-xs text-green-500 mt-0.5">Cubierto</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleDetails(calc.empleadoId)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title={isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
                          >
                            <ChevronRight
                              size={15}
                              className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            />
                          </button>
                        </td>
                      </tr>

                      {/* ── Expanded detail row ──────────────────── */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={14} className="px-4 py-4 bg-gray-50 border-b border-gray-100">
                            <div className="space-y-4">

                              {/* Ingresos Variables */}
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                  Ingresos Variables — {selectedPeriod}
                                </p>
                                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                  {[
                                    { label: 'Horas Extras',      value: calc.horasExtras,     highlight: false },
                                    { label: 'Comisiones',        value: calc.comisiones,      highlight: false },
                                    { label: 'Incentivos',        value: calc.incentivos,      highlight: false },
                                    { label: 'Vacaciones',        value: calc.vacaciones,      highlight: false },
                                    { label: 'Bono Desempeño',    value: calc.bonificaciones,  highlight: false },
                                    { label: 'Otros Ingresos',    value: calc.otrosIngresos,   highlight: false },
                                    { label: 'Bonif. Anual',      value: calc.bonificacionAnual, highlight: true },
                                  ].map(({ label, value, highlight }) => (
                                    <div key={label} className={`border rounded-lg p-2.5 ${highlight && value > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
                                      <p className={`text-xs mb-1 ${highlight && value > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                                        {label}{highlight && value > 0 ? ' *' : ''}
                                      </p>
                                      <p className={`text-sm font-semibold ${value > 0 ? (highlight ? 'text-amber-700' : 'text-gray-900') : 'text-gray-300'}`}>
                                        RD$ {fmt(value)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* TSS Deductible */}
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                  Deducciones de Base Imponible
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                  {[
                                    { label: 'AFP Empleado',        value: calc.afpMensual              },
                                    { label: 'SFS Empleado',        value: calc.sfsMensual              },
                                    { label: 'Cápita ARS',          value: calc.capitaMensual           },
                                    { label: 'Interés Hipotecario', value: calc.mortgageInterestMensual  },
                                    { label: 'Total Deductible',    value: calc.tssDeductibleISR, bold: true },
                                  ].map(({ label, value, bold }) => (
                                    <div key={label} className="bg-white border border-gray-200 rounded-lg p-2.5">
                                      <p className="text-xs text-gray-400 mb-1">{label}</p>
                                      <p className={`text-sm ${bold ? 'font-bold text-amber-700' : 'font-medium text-gray-700'}`}>
                                        RD$ {fmt(value)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Beneficios Fiscales (tabla) */}
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                  Beneficios Fiscales — Año {new Date().getFullYear()}
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {[
                                    {
                                      label: 'Interés Hipotecario',
                                      value: calc.mortgageInterestMensual,
                                      annual: calc.mortgageAnnual,
                                      effect: 'Reduce base imponible',
                                      effectCls: 'text-blue-600',
                                      Icon: Home,
                                      iconBg: 'bg-blue-100', iconCls: 'text-blue-600'
                                    },
                                    {
                                      label: 'Gastos Educativos',
                                      value: calc.educationCreditMensual,
                                      annual: calc.educationAnnual,
                                      effect: 'Crédito contra ISR',
                                      effectCls: 'text-green-600',
                                      Icon: GraduationCap,
                                      iconBg: 'bg-green-100', iconCls: 'text-green-600'
                                    },
                                    {
                                      label: 'Dependiente Adicional',
                                      value: calc.dependentCreditMensual,
                                      annual: calc.dependentAnnual,
                                      effect: 'Crédito contra ISR',
                                      effectCls: 'text-purple-600',
                                      Icon: Users,
                                      iconBg: 'bg-purple-100', iconCls: 'text-purple-600'
                                    },
                                  ].map(({ label, value, annual, effect, effectCls, Icon: BenIcon, iconBg, iconCls }) => (
                                    <div key={label} className={`border rounded-lg p-3 ${value > 0 ? 'bg-white border-gray-200' : 'bg-gray-50 border-dashed border-gray-200'}`}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className={`w-6 h-6 ${value > 0 ? iconBg : 'bg-gray-100'} rounded-md flex items-center justify-center flex-shrink-0`}>
                                          <BenIcon size={12} className={value > 0 ? iconCls : 'text-gray-300'} />
                                        </div>
                                        <p className={`text-xs font-medium ${value > 0 ? 'text-gray-700' : 'text-gray-400'}`}>{label}</p>
                                      </div>
                                      {value > 0 ? (
                                        <>
                                          <p className="text-sm font-bold text-gray-900">RD$ {fmt(value)}<span className="text-xs font-normal text-gray-400">/mes</span></p>
                                          <p className="text-xs text-gray-400 mt-0.5">Anual: RD$ {fmt(annual)}</p>
                                          <p className={`text-xs font-medium mt-1 ${effectCls}`}>{effect}</p>
                                        </>
                                      ) : (
                                        <p className="text-xs text-gray-400 italic">No registrado</p>
                                      )}
                                    </div>
                                  ))}
                                  {/* ISR después de créditos */}
                                  <div className="border border-gray-200 rounded-lg p-3 bg-white">
                                    <p className="text-xs font-medium text-gray-500 mb-2">Resumen de créditos</p>
                                    <p className="text-xs text-gray-500">ISR bruto: <span className="font-semibold text-gray-700">RD$ {fmt(calc.isrBruto)}</span></p>
                                    <p className="text-xs text-green-600 mt-0.5">Créditos: <span className="font-semibold">-RD$ {fmt(calc.isrCreditos)}</span></p>
                                    <p className="text-sm font-bold text-red-600 mt-1.5 border-t pt-1.5">ISR Final: RD$ {fmt(calc.isrMensual)}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Resumen ISR */}
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                  Resumen ISR
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {[
                                    { label: 'Renta Imponible',      value: calc.rentaImponible,   cls: 'text-gray-900', sub: 'Total ingresos − TSS' },
                                    { label: 'Renta Anual (Regular)', value: calc.rentaAnual,       cls: 'text-gray-900', sub: 'Sin bonificación anual' },
                                    { label: 'ISR Mensual Total',      value: calc.isrMensualTotal,       cls: 'text-red-600',  sub: calc.bonificacionAnual > 0 ? `Reg: ${fmt(calc.isrMensual)} + Bonif: ${fmt(calc.isrBonifProyectado)}` : 'Ingresos regulares' },
                                    { label: 'ISR Anual Proyectado',   value: calc.isrMensualTotal * 12,  cls: 'text-gray-900', sub: 'ISR Mensual Total × 12' },
                                  ].map(({ label, value, cls, sub }) => (
                                    <div key={label} className="border border-gray-200 rounded-lg p-2.5 bg-white">
                                      <p className="text-xs text-gray-400 mb-1">{label}</p>
                                      <p className={`text-sm font-semibold ${cls}`}>RD$ {fmt(value)}</p>
                                      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
                                    </div>
                                  ))}
                                </div>
                                {/* Proyección con bonificación anual (cuando aplica) */}
                                {calc.bonificacionAnual > 0 && (
                                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                                    <p className="text-xs font-semibold text-amber-700 mb-1.5">Proyección Anual con Bonificación (informativo)</p>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <p className="text-[10px] text-amber-600">Renta Anual c/Bonif.</p>
                                        <p className="text-sm font-semibold text-amber-800">RD$ {fmt(calc.rentaAnualConBonif)}</p>
                                        <p className="text-[10px] text-amber-500">Regular×12 + Bonif. anual</p>
                                      </div>
                                      <div>
                                        <p className="text-[10px] text-amber-600">ISR Mensual c/Bonif.</p>
                                        <p className="text-sm font-semibold text-amber-800">RD$ {fmt(calc.isrConBonif)}</p>
                                        <p className="text-[10px] text-amber-500">Proyección distribuida</p>
                                      </div>
                                      <div>
                                        <p className="text-[10px] text-amber-600">ISR Bonif. Retenido</p>
                                        <p className={`text-sm font-semibold ${calc.isrRetenidoBonif > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                                          {calc.isrRetenidoBonif > 0 ? `RD$ ${fmt(calc.isrRetenidoBonif)}` : 'No pagada aún'}
                                        </p>
                                        <p className="text-[10px] text-amber-500">Del módulo bonificaciones</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {/* ISR retenido / pendiente */}
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                                    <p className="text-xs text-green-600 font-medium mb-1">ISR Nóminas (Q1+Q2)</p>
                                    <p className="text-sm font-bold text-green-700">RD$ {fmt(calc.isrRetenidoNomina)}</p>
                                    {calc.nominasProcesadas > 0 && (
                                      <p className="text-xs text-green-500 mt-0.5">
                                        Q1: RD${fmt(calc.isrRetenidoQ1)} · Q2: RD${fmt(calc.isrRetenidoQ2)}
                                      </p>
                                    )}
                                    {calc.nominasProcesadas > 0 && (
                                      <p className={`text-[10px] mt-0.5 font-medium ${Math.abs(calc.isrRetenidoNomina - calc.isrMensual) <= 1 ? 'text-green-600' : 'text-amber-500'}`}>
                                        {Math.abs(calc.isrRetenidoNomina - calc.isrMensual) <= 1
                                          ? '✓ Q1+Q2 = ISR mensual'
                                          : `Δ ${fmt(Math.abs(calc.isrRetenidoNomina - calc.isrMensual))} vs ISR mensual`}
                                      </p>
                                    )}
                                    {calc.isrRetenidoBonif > 0 && (
                                      <p className="text-xs text-orange-600 mt-0.5 font-medium">
                                        + RD${fmt(calc.isrRetenidoBonif)} bonif. (módulo bonif.)
                                      </p>
                                    )}
                                  </div>
                                  <div className={`border rounded-lg p-2.5 ${calc.isrPendiente === 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                                    <p className={`text-xs font-medium mb-1 ${calc.isrPendiente === 0 ? 'text-green-600' : 'text-amber-600'}`}>ISR Pendiente</p>
                                    <p className={`text-sm font-bold ${calc.isrPendiente === 0 ? 'text-green-600' : 'text-amber-700'}`}>
                                      {calc.isrPendiente === 0 ? 'Todo retenido ✓' : `RD$ ${fmt(calc.isrPendiente)}`}
                                    </p>
                                  </div>
                                  <div className="bg-white border border-gray-200 rounded-lg p-2.5">
                                    <p className="text-xs text-gray-400 mb-1">Cobertura nóminas</p>
                                    <p className="text-sm font-bold text-gray-700">
                                      {calc.isrAcumulado > 0
                                        ? `${Math.min(100, Math.round((calc.isrRetenidoNomina / calc.isrAcumulado) * 100))}%`
                                        : '—'}
                                    </p>
                                    {calc.isrAcumulado > 0 && (
                                      <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full transition-all ${calc.isrPendiente === 0 ? 'bg-green-500' : 'bg-amber-500'}`}
                                          style={{ width: `${Math.min(100, Math.round((calc.isrRetenidoNomina / calc.isrAcumulado) * 100))}%` }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Crédito Fiscal e ISR Final */}
                              {calc.creditoFiscal > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                    Crédito Fiscal (Saldo a Favor)
                                  </p>
                                  <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                                      <div className="flex items-center gap-1 mb-1">
                                        <CreditCard size={10} className="text-blue-500" />
                                        <p className="text-xs text-blue-600 font-medium">Saldo Disponible</p>
                                      </div>
                                      <p className="text-sm font-bold text-blue-700">RD$ {fmt(calc.creditoFiscal)}</p>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-2.5">
                                      <p className="text-xs text-gray-400 mb-1">ISR a Retener (bruto)</p>
                                      <p className="text-sm font-semibold text-red-500">RD$ {fmt(calc.isrARetener)}</p>
                                    </div>
                                    <div className={`border rounded-lg p-2.5 ${calc.isrFinal === 0 ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                                      <p className="text-xs text-gray-400 mb-1">ISR Final a Retener</p>
                                      <p className={`text-sm font-bold ${calc.isrFinal === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        RD$ {fmt(calc.isrFinal)}
                                      </p>
                                      {calc.isrFinal === 0 && (
                                        <p className="text-xs text-green-500 mt-0.5">Crédito cubre el ISR completo</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {calculations.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td colSpan={3} className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Totales
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                    RD$ {fmt(totals.totalIngresos)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">
                    RD$ {fmt(totals.tssTotal)}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-400">
                    {calculations.filter(c => c.mortgageInterestMensual > 0 || c.educationCreditMensual > 0 || c.dependentCreditMensual > 0).length} con beneficios
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                    RD$ {fmt(totals.rentaImponible)}
                  </td>
                  <td />
                  <td className="px-4 py-3 text-right text-sm font-bold text-red-600">
                    RD$ {fmt(totals.isrTotal)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-green-700 bg-green-50">
                    RD$ {fmt(totals.isrYaRetenidoTotal)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold bg-amber-50">
                    <span className={totals.isrPendienteTotal === 0 ? 'text-green-600' : 'text-amber-700'}>
                      {totals.isrPendienteTotal === 0 ? 'Al día ✓' : `RD$ ${fmt(totals.isrPendienteTotal)}`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-blue-700 bg-blue-50">
                    {totals.creditoFiscalTotal > 0 ? `− RD$ ${fmt(totals.creditoFiscalTotal)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-red-600 bg-blue-50">
                    RD$ {fmt(totals.isrFinalTotal)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

    </div>
  );
}

// Función exportada para procesamiento de nómina
export function calculateEmployeeISR(totalIngresos, tssEmpleado) {
  const rentaImponible = totalIngresos - tssEmpleado;
  const rentaAnual = rentaImponible * 12;

  if (rentaAnual < 416220)     return 0;
  if (rentaAnual < 624329.01)  return ((rentaAnual - 416220.01) * 0.15) / 12;
  if (rentaAnual < 867123.01)  return (((rentaAnual - 624329.01) * 0.20) + 31216) / 12;
  return (((rentaAnual - 867123.01) * 0.25) + 79776) / 12;
}
